import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FilterViewList, FilterViewType, MutationType } from 'shared/types';
import request from 'utils/request';

export const useSaveFilterView = ({
  setToast,
  callback,
  filter_view_type = 'CONTACT',
}: { filter_view_type?: string } & MutationType) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  return useMutation(
    async (filterView: FilterViewType) => {
      const payload = {
        ...filterView,
        filter_view_type: filter_view_type,
        name: filterView.name.trim(),
      };
      let data: FilterViewType;
      if (filterView.id) {
        data = await request(`/filter-views/${filterView.id}`, payload, 'PUT');
      } else {
        data = await request('/filter-views', payload, 'POST');
      }
      return data;
    },
    {
      onSuccess: (response: FilterViewType, filterView: FilterViewType) => {
        queryClient.invalidateQueries(
          `filters-${filter_view_type?.toLowerCase()}`,
        );
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: filterView.id
              ? t('forms.toast.success.filter_view_updated')
              : t('forms.toast.success.filter_view_created'),
          });
        if (callback) callback(true, response, !filterView.id);
      },
      onError: (error: any) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text:
              error.response.status == 400
                ? t('forms.toast.error.filter_view_exists')
                : t('forms.toast.error.filter_view_creation_error'),
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useGetFilterViews = ({
  page_size = 50,
  callback = () => {},
  filter_view_type,
}: {
  page_size?: number;
  callback?: MutationType['callback'];
  filter_view_type?: string;
}) => {
  return useQuery<FilterViewList>(
    [`filters-${filter_view_type?.toLowerCase()}`],
    async () => {
      const data: FilterViewList = await request(
        `/filter-views?page_size=${page_size}${
          filter_view_type ? '&filter_view_type=' + filter_view_type : ''
        }`,
        {},
        'GET',
      );
      return data;
    },
    {
      onSuccess: (response: FilterViewList) => {
        if (callback) callback(true, response);
      },
    },
  );
};

export const useGetFilterView = ({
  page_size = 50,
  callback = () => {},
  filterViewId,
}: {
  page_size?: number;
  callback?: MutationType['callback'];
  filterViewId?: string;
}) => {
  return useQuery<FilterViewType>(
    [`filterView-${filterViewId}`],
    async () => {
      const data: FilterViewType = await request(
        `/filter-view/${filterViewId}`,
        {},
        'GET',
      );
      return data;
    },
    {
      enabled: !!filterViewId,
      onSuccess: (response: FilterViewType) => {
        callback?.(true, response);
      },
      onError: (response: any) => {
        callback?.(false, response);
      },
    },
  );
};

export const useDeleteFilterView = ({ setToast, callback }: MutationType) => {
  const { t } = useTranslation();

  return useMutation(
    async (filterViewId: string) => {
      if (filterViewId) {
        const data: boolean = await request(
          `/filter-views/${filterViewId}`,
          {},
          'DELETE',
        );
        return data;
      }
    },
    {
      onSuccess: (response, filterViewId: string) => {
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: t('forms.toast.success.filter_view_deleted'),
          });
        if (callback) callback(true, response, filterViewId);
      },
      onError: (error) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: t('forms.toasts.error.filter_view_deletion_failed'),
          });
        if (callback) callback(false, error);
      },
    },
  );
};
